import { countryLabelKeys } from "@/lib/constants/locale";
import { BLOCKED_PATIENTPATH_COUNTRIES } from "@/lib/constants/blockedPatientPath";
import type { EmeaEntity } from "@/lib/constants/emeaEntities";

export type LegalDocumentKey =
  | "hcpPrivacy"
  | "patientPrivacy"
  | "collectionStatement"
  | "termsOfUse";

// Order the documents appear in the tab bar / mobile nav.
export const LEGAL_DOCUMENT_KEYS: LegalDocumentKey[] = [
  "hcpPrivacy",
  "patientPrivacy",
  "collectionStatement",
  "termsOfUse",
];

// Public-facing URL slug for each document's dedicated route
export const LEGAL_DOCUMENT_SLUGS: Record<LegalDocumentKey, string> = {
  hcpPrivacy: "hcp-privacy",
  patientPrivacy: "patient-privacy",
  collectionStatement: "collection-statement",
  termsOfUse: "terms-of-use",
};

export interface LegalDocumentProps {
  onNavigate?: (doc: LegalDocumentKey) => void;
}

// Documents that depend on VELYS Patient Path. Countries without Patient Path
// (BLOCKED_PATIENTPATH_COUNTRIES) only get the HCP Privacy Policy.
const PATIENTPATH_LEGAL_DOCUMENTS: LegalDocumentKey[] = [
  "patientPrivacy",
  "collectionStatement",
  "termsOfUse",
];

// The legal documents available in the detected country, in tab order.
export const getVisibleLegalDocuments = (
  country: string
): LegalDocumentKey[] =>
  BLOCKED_PATIENTPATH_COUNTRIES.includes(country)
    ? LEGAL_DOCUMENT_KEYS.filter(
        (doc) => !PATIENTPATH_LEGAL_DOCUMENTS.includes(doc)
      )
    : LEGAL_DOCUMENT_KEYS;

export type Region = "us" | "anz" | "emea";

const EMEA_COUNTRIES = ["GB", "AT", "CH", "DE", "IE"];
const ANZ_COUNTRIES = ["AU", "NZ"];

export const countryToRegion = (country: string): Region => {
  if (EMEA_COUNTRIES.includes(country)) return "emea";
  if (ANZ_COUNTRIES.includes(country)) return "anz";
  return "us";
};

// The `countryLabels` i18n key for a detected country code, or null
export const getCountryLabelKey = (countryCode: string): string | null =>
  countryLabelKeys[countryCode] ?? null;

export type LegalPart =
  | { kind: "subtitle"; key: string }
  | { kind: "heading"; key: string }
  | { kind: "paragraph"; key: string }
  | { kind: "list"; key: string };

export const lp = {
  subtitle: (key: string): LegalPart => ({ kind: "subtitle", key }),
  heading: (key: string): LegalPart => ({ kind: "heading", key }),
  paragraph: (key: string): LegalPart => ({ kind: "paragraph", key }),
  list: (key: string): LegalPart => ({ kind: "list", key }),
};

export const applyEntity = (text: string, entity: EmeaEntity | null): string =>
  entity
    ? text
        .replace(/\{\{entityName\}\}/g, entity.entityName)
        .replace(/\{\{entityAddress\}\}/g, entity.entityAddress)
    : text;
